/*
 * Last-resort end-of-service values, used only when the app cannot reach the server at all.
 *
 * Normally the server decides everything: the notice and its `validUntil` come from the
 * backend and are stored on the device. But the backend will eventually be switched off after
 * the service has closed, and a member who reinstalls the app (or clears its data) after that
 * has nothing stored and gets no answer from the server. Without these values such an app
 * would behave as if nothing had happened and only show network errors, instead of telling the
 * member that the service has closed.
 *
 * IMPORTANT: FALLBACK_SERVICE_END_AT duplicates `valid_until` in the backend configuration.
 * If the closing moment changes, changing the backend configuration is NOT enough — this value
 * has to be changed too and a new app version released, because the app has no over-the-air
 * update mechanism. Set it to null to switch the fallback off completely.
 */

/** End of service as an ISO date time (UTC). 2026-08-31 16:00 Finnish time. */
export const FALLBACK_SERVICE_END_AT: string | null = '2026-08-31T13:00:00Z';

/**
 * Link shown on the end-of-service screen when there is no stored notice to take the link
 * from. Same address as the more-info link served by the backend.
 */
export const FALLBACK_MORE_INFO_URL =
    'https://www.mehilainen.fi/lehdistotiedotteet/heltin-tyoterveyspalvelut-osaksi-mehilaista';

/**
 * True when the given end-of-service moment has passed. A missing or unparseable value means
 * "no fallback", so a mistake in the constant can never close a working app.
 */
export function serviceEndHasPassed(endAt: string | null): boolean {
    if (!endAt) {
        return false;
    }

    const endAtMs = Date.parse(endAt);

    return !Number.isNaN(endAtMs) && Date.now() >= endAtMs;
}

/** True when the fallback end of service configured above has passed. */
export function fallbackServiceEndHasPassed(): boolean {
    return serviceEndHasPassed(FALLBACK_SERVICE_END_AT);
}
